import { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import useGetGlassBottleReturnRate from "./useGetGlassBottleReturnRate";
import useUpdateGlassBottleReturnRate from "../UpdateGlassBottleReturnRate/useUpdateGlassBottleReturnRate";
import useGetCompany from "Features/Company/ReadCompany/useGetCompany";
import { useParams } from "react-router-dom";
import { Header, Segment, Button, Icon } from "semantic-ui-react";
import { ButtonContainer, EditBtn } from "Components/Buttons";
import { StateContext } from "Application/state/StateContext";
import { PageTitle } from "Components/Titles";
import useSurvey from "Features/Survey/useSurvey";
import { YesNoModal } from "Components/Modals";
import { ListCompanySurveyAttachements } from "Features/CompanyAttachement";

function ReadGlassBottleReturnRate () {

    const { t } = useTranslation();
    const { userProfile } = useContext(StateContext);
    const { companyId, surveyId } = useParams();
    const { glassBottleReturnRate, getGlassBottleReturnRateById } = useGetGlassBottleReturnRate();
    const { company, getCompanyById } = useGetCompany();
    const [ isLoading, setIsLoading ] = useState(true);
    const { isEditingAllowed } = useSurvey();
    const { updateGlassBottleReturnRate } = useUpdateGlassBottleReturnRate();
    const [ lockSurveyModalOpen, setLockSurveyModalOpen ] = useState(false);
    const [ openSurveyModalOpen, setOpenSurveyModalOpen ] = useState(false);
    const [ isLocked, setIsLocked ] = useState(true);
    const [ isOpen, setIsOpen ] = useState(true);
    const [ isEditable, setIsEditable ] = useState(false);
    const [ deadline, setDeadline ] = useState<null | Date>(null);

    const isAdmin = userProfile && userProfile.roles && userProfile.roles.find((item:any) => item.name==="Admin") ? true : false;

    // Fetch glass bottle return rate
    useEffect(() => {
        (async () => {
            if(companyId || userProfile.companyId){
                await getGlassBottleReturnRateById(companyId || userProfile.companyId,surveyId);
            }
            setIsLoading(false);
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[userProfile.companyId]);

    // Fetch company
    useEffect(() => {
        (async () => {
            if(glassBottleReturnRate.companyId){
                await getCompanyById(glassBottleReturnRate.companyId);
                setIsEditable(isEditingAllowed(glassBottleReturnRate.isPermanentlyLocked,glassBottleReturnRate.openDate,glassBottleReturnRate.closeDate))
                setIsLocked(glassBottleReturnRate.isPermanentlyLocked);
                setIsOpen(new Date(glassBottleReturnRate.closeDate)>new Date());
                let dl = new Date(glassBottleReturnRate.closeDate);
                dl.setSeconds(dl.getSeconds()-1);
                setDeadline(dl);
                setIsLoading(false);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[glassBottleReturnRate]);

    const lockSurvey = async (answer:boolean) => {
        setLockSurveyModalOpen(false);
        setIsLoading(true);

        const glassBottleReturnRate = {
            isPermanentlyLocked: true
        }
        let result = await updateGlassBottleReturnRate(companyId || userProfile.companyId,surveyId,glassBottleReturnRate);
        if (result && result.status === 204) {
            setIsEditable(false);
            setIsLocked(true);
        }
        setIsLoading(false);
        window.scrollTo(0,0);
    }

    const openSurvey = async (answer:boolean) => {
        setOpenSurveyModalOpen(false);
        setIsLoading(true);

        // open survey until the end of next day
        let closedate = new Date()
        closedate = new Date(closedate.getFullYear(),closedate.getMonth(),closedate.getDate()+2);
        const glassBottleReturnRate = {
            closeDate: closedate
        }
        let result = await updateGlassBottleReturnRate(companyId || userProfile.companyId,surveyId,glassBottleReturnRate);
        if (result && result.status === 204) {
            setIsOpen(true);
            closedate.setSeconds(closedate.getSeconds()-1);
            setDeadline(closedate);
        }
        
        setIsLoading(false);
        window.scrollTo(0,0);
    }

    return (
        <>
        <YesNoModal 
            type="save"
            header={t("features.survey.lockSurvey")}
            text={t("features.survey.lockSurveyConfirm")}
            method={lockSurvey}
            open={lockSurveyModalOpen}
            setOpen={setLockSurveyModalOpen}
            item=""
            noClick={false}
            handleClose={true}
            disableYesButton={false}
        />
        <YesNoModal 
            type="save"
            header={t("features.survey.openSurvey")}
            text={t("features.survey.openSurveyConfirm")}
            method={openSurvey}
            open={openSurveyModalOpen}
            setOpen={setOpenSurveyModalOpen}
            item=""
            noClick={false}
            handleClose={true}
            disableYesButton={false}
        />
        <PageTitle title={glassBottleReturnRate.name!=='' ? glassBottleReturnRate.name : t("pages.glassBottleReturnRateDetail")} />
        <Segment className="invisible" loading={isLoading}>
            {glassBottleReturnRate.id ?
                <>
                <Header as="h2">{t("features.glassBottleReturnRate.glassBottles")}</Header>
                {isAdmin && 
                    <>
                    <Header sub>{t("features.glassBottleReturnRate.deadline")}</Header>
                    <div>{deadline?.toLocaleDateString("fi-FI")}</div>
                    </>
                }
                {companyId &&
                    <>
                    <Header sub>{t("features.glassBottleReturnRate.company")}</Header>
                    <div>{company && company.name}</div>
                    </>
                }

                <Header sub>{t("features.glassBottleReturnRate.initialStock")} {glassBottleReturnRate && new Date(glassBottleReturnRate.initialStockDate).toLocaleDateString("fi-FI")}</Header>
                <div>{glassBottleReturnRate && glassBottleReturnRate.initialStock} {t("features.glassBottleReturnRate.pcs")}</div>

                <Header sub>{t("features.glassBottleReturnRate.procurement")}</Header>
                <div>{glassBottleReturnRate && glassBottleReturnRate.procurement} {t("features.glassBottleReturnRate.pcs")}</div>

                <Header sub>{t("features.glassBottleReturnRate.salesToCustomers")}</Header>
                <div>{glassBottleReturnRate && glassBottleReturnRate.salesToCustomers} {t("features.glassBottleReturnRate.pcs")}</div>

                <Header sub>{t("features.glassBottleReturnRate.purchasesFromCustomers")}</Header>
                <div>{glassBottleReturnRate && glassBottleReturnRate.purchasesFromCustomers} {t("features.glassBottleReturnRate.pcs")}</div>

                <Header sub>{t("features.glassBottleReturnRate.salesToCompanies")}</Header>
                <div>{glassBottleReturnRate && glassBottleReturnRate.salesToCompanies} {t("features.glassBottleReturnRate.pcs")}</div>

                <Header sub>{t("features.glassBottleReturnRate.purchasesFromCompanies")}</Header>
                <div>{glassBottleReturnRate && glassBottleReturnRate.purchasesFromCompanies} {t("features.glassBottleReturnRate.pcs")}</div>

                <Header sub>{t("features.glassBottleReturnRate.scrapping")}</Header>
                <div>{glassBottleReturnRate && glassBottleReturnRate.scrapping} {t("features.glassBottleReturnRate.pcs")}</div>

                <Header sub>{t("features.glassBottleReturnRate.closingStock")} {glassBottleReturnRate && new Date(glassBottleReturnRate.closingStockDate).toLocaleDateString("fi-FI")}</Header>
                <div>{glassBottleReturnRate && glassBottleReturnRate.closingStock} {t("features.glassBottleReturnRate.pcs")}</div>

                <Header sub>{t("features.glassBottleReturnRate.loss")}</Header>
                <div>{glassBottleReturnRate && glassBottleReturnRate.loss} {t("features.glassBottleReturnRate.pcs")}</div>

                <ListCompanySurveyAttachements 
                    companyId={userProfile.companyId}
                    surveyId={surveyId}
                    surveyTypeRef={"glassbottlereturnrate"}
                />

                <ButtonContainer>
                    <EditBtn to={"edit"} disabled={!isEditable} />
                    {isAdmin &&
                        <>
                        <Button
                            icon
                            className='extended-button lock-button'
                            labelPosition="left"
                            onClick={() => setLockSurveyModalOpen(true) }
                            disabled={isLocked}
                        >
                            <Icon name={'lock'} />
                            {t("features.survey.lock")}
                        </Button>
                        <Button
                            icon
                            className='extended-button date-button'
                            labelPosition="left"
                            onClick={() => setOpenSurveyModalOpen(true) }
                            disabled={isLocked || isOpen}
                        >
                            <Icon name={'calendar'} />
                            {t("features.survey.openSurvey")}
                        </Button>
                        </>
                    }
                </ButtonContainer>
                </>
            :
                <p>{t("features.survey.notFound")}</p>
            }

        </Segment>
        </>
    );
}

export default ReadGlassBottleReturnRate;
